import { ref } from 'vue';
import { useImageCacheStore } from '@/src/store/image-cache';
import { useSegmentationStore } from '@/src/segmentation/store';
import { useSegmentStore } from '@/src/segmentation/segments';
import { boundScalars } from '@/src/segmentation/masks/overlap';
import { emptyExtent, growExtent, extentContainsIndex, maskOffset } from '@/src/segmentation/geometry';
import { observePaintStrokes } from '@/src/segmentation/editing/strokeEvents';
import { applySliceFill, type SliceFill } from './sliceFill';
type Voxel = { i: number; j: number; k: number; before: string[]; after: string[] };
type Stroke = { imageId: string; voxels: Voxel[]; fill?: undefined } | { imageId: string; fill: SliceFill; voxels?: undefined };
const same = (a: string[], b: string[]) => a.length === b.length && a.every((id, i) => id === b[i]);

export function createBrushHistory(imageId: () => string, changed: () => void, voxelLimit = 200000) {
  const undoCount = ref(0); const redoCount = ref(0); const notice = ref('');
  let undo: Stroke[] = []; let redo: Stroke[] = []; let captured = new Map<number, Voxel>();
  const segmentation = useSegmentationStore();
  function updateCounts() { undoCount.value = undo.length; redoCount.value = redo.length; }
  function record(stroke: Stroke) {
    undo.push(stroke); redo = [];
    const bytes = (command: Stroke) => command.fill ? command.fill.indices.byteLength : command.voxels.length * 96;
    while (undo.length > 40 || (undo.length > 1 && undo.reduce((count, command) => count + bytes(command),0) > 24*1024*1024)) undo.shift();
    updateCounts(); changed();
  }
  function clear() { undo = []; redo = []; captured.clear(); notice.value = ''; updateCounts(); }
  const dispose = observePaintStrokes({
    begin: id => { if (id === imageId()) { captured.clear(); notice.value = ''; } },
    beforeVoxel: (id, i, j, k) => {
      if (id !== imageId()) return;
      const dims = useImageCacheStore().getVtkImageData(id)!.getDimensions();
      const offset = i + dims[0] * (j + dims[1] * k);
      if (captured.has(offset)) return;
      if (captured.size >= voxelLimit) { notice.value = '该笔已达到可撤销体素上限，请松开鼠标后分次绘制。已绘制部分仍可整笔撤销。'; return false; }
      captured.set(offset, { i, j, k, before: segmentation.segmentsAt(id, i, j, k), after: [] });
    },
    end: id => {
      if (id !== imageId()) return;
      const voxels = [...captured.values()].filter(v => { v.after = segmentation.segmentsAt(id, v.i, v.j, v.k); return !same(v.before, v.after); });
      captured.clear();
      if (!voxels.length) return;
      record({ imageId: id, voxels });
    },
  });
  function apply(stroke: Stroke, side: 'before' | 'after') {
    if (stroke.fill) {
      try { applySliceFill(stroke.imageId,stroke.fill,side === 'before'); changed(); return true; }
      catch (error) { notice.value = (error as Error).message; return false; }
    }
    const ids = new Set(stroke.voxels.flatMap(v => [...v.before, ...v.after]));
    if ([...ids].some(id => useSegmentStore().segments.getSegment(id)?.locked)) {
      notice.value = '请先解锁受影响的标签，再撤销或重做该笔。';
      return false;
    }
    for (const segmentId of ids) {
      const writes = stroke.voxels.filter(v => v[side].includes(segmentId));
      let mask = segmentation.maskFor(stroke.imageId, segmentId);
      if (!mask && !writes.length) continue;
      mask ??= segmentation.createMask(segmentation.ensureSegmentationForImage(stroke.imageId).id, segmentId);
      const voxels = segmentation.maskVoxels(mask.id); voxels.materialize();
      if (writes.length) {
        const extent = emptyExtent(); writes.forEach(v => growExtent(extent, v.i, v.j, v.k)); voxels.ensureContains(extent);
      }
      const bounded = boundScalars(voxels.binding());
      if (!bounded) continue;
      for (const v of stroke.voxels) if (extentContainsIndex(bounded.extent, v.i, v.j, v.k)) bounded.scalars[maskOffset(bounded, v.i, v.j, v.k)] = v[side].includes(segmentId) ? 1 : 0;
      voxels.image().modified();
    }
    changed();
    return true;
  }
  function undoStroke() { const stroke = undo.at(-1); if (stroke && apply(stroke, 'before')) { undo.pop(); redo.push(stroke); updateCounts(); } }
  function redoStroke() { const stroke = redo.at(-1); if (stroke && apply(stroke, 'after')) { redo.pop(); undo.push(stroke); updateCounts(); } }
  return { undoCount, redoCount, notice, clear, dispose, undoStroke, redoStroke,
           recordFill: (fill: SliceFill) => record({ imageId: imageId(), fill }) };
}
